import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query'
import { ApiResult } from '../types'

type UsePortalMutationOptions<TData, TError = Error, TVariables = void, TContext = unknown> = Omit<
  UseMutationOptions<TData | null, TError, TVariables, TContext>,
  'mutationFn'
> & {
  mutationFn: (variables: TVariables) => Promise<ApiResult<TData>>
}

/**
 * A wrapper around react-query's useMutation that automatically handles ApiResult unwrapping.
 * The mutationFn should return a Promise<ApiResult<T>> — no need to manually check hasErrors.
 * If the ApiResult has errors, the error is thrown and can be caught via the onError callback.
 */
const usePortalMutation = <TData, TError = Error, TVariables = void, TContext = unknown>(
  options: UsePortalMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData | null, TError, TVariables, TContext> => {
  const { mutationFn, ...rest } = options
  return useMutation<TData | null, TError, TVariables, TContext>({
    ...rest,
    mutationFn: async variables => {
      const { error, hasErrors, value } = await mutationFn(variables)
      if (hasErrors) throw (error as TError) ?? new Error('An unexpected error occurred')
      return value
    },
  })
}

export default usePortalMutation
